import { Record, RecordOf } from "immutable";
import * as GQL from "src/types/graphql";

export interface SchoolFormId {
  formId: uuid;
  schoolId: uuid | null;
  formSchoolRankId: uuid | null;
}

export interface FormIdListResult {
  cacheKey: uuid | null;
  items: SchoolFormId[];
}

/**
 * Payload for async bulk-action create. Mirrors form-service
 * `CreateBulkActionInput`:
 * - inclusion: checkbox selection — no Redis snapshot
 * - exclusion: select-all — requires listFormIdsV2 cacheKey
 */
export type BulkTargets =
  | {
      inclusion: SchoolFormId[];
      formTemplateId: uuid;
    }
  | {
      cacheKey: uuid;
      exclusion: SchoolFormId[];
      formTemplateId: uuid;
    };

export interface FormId {
  formId: uuid;
}

export const SchoolFormKeyRecordFactory = Record<SchoolFormId>({
  formId: "",
  schoolId: null,
  formSchoolRankId: null,
});

export type SchoolFormKeyRecord = RecordOf<SchoolFormId>;

export interface FormSchoolStatus {
  id: uuid;
  form_id: uuid;
  school_id: uuid;
  sub_status: string | null;
  form: {
    status: GQL.form_status_enum;
  };
  offers: {
    id: uuid;
    status: GQL.offer_status_enum;
    expires_at: string | null;
    assigned_forms: {
      id: uuid;
    }[];
  }[];
  waitlists: {
    id: uuid;
    status: GQL.waitlist_status_enum;
    assigned_forms: {
      id: uuid;
    }[];
  }[];
}

type Person = {
  id: uuid;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  referenceId?: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
};

type Student = Person & {
  birthDate: string | null;
};

type Guardian = Person & {
  emailAddress: string | null;
  phoneNumber: string | null;
  preferredLanguage: string | null;
};

export type GradeConfig = {
  id: uuid;
  label: string;
};

export type School = {
  id: uuid;
  name: string;
  referenceId?: string | null;
};

export type AttendingSchool = School & {
  status: GQL.attendance_status_type_enum;
};

export type FormTag = {
  name: string;
  createdAt: timestamptz | null;
  tagGroupId: string | null;
};

export type VerificationResult = {
  verificationId: uuid;
  verificationStatus: GQL.verification_status_enum;
};

export type FormListForm = {
  id: uuid;
  status: GQL.form_status_enum;
  statusUpdatedAt: timestamptz | null;
  isHiddenFromParent: boolean;
  grade: GradeConfig | null;
  tags: FormTag[];
  verificationResults: VerificationResult[];
  submittedAt: timestamptz | null;
  errorCount: number;
};

export type FormListSchoolForm = {
  id: uuid;
  subStatus: string | null;
  subStatusUpdatedAt: timestamptz | null;
  rank: number | null;
  tags: FormTag[];
  tiebreaker: number | null;
  lotteryNumber: number | null;
  lotteryPosition: number | null;
  offer: {
    expiresAt: timestamptz | null;
    program: {
      id: uuid;
      name: string;
    } | null;
  } | null;
  waitlist: {
    position: number | null;
  } | null;
  schoolSubmittedAt: timestamptz | null;
};

export type FormListRow = {
  id: SchoolFormId;
  form: FormListForm;
  student: Student;
  // For linked forms, this is the school of the previous offer/waitlist.
  school: School | null;
  schoolForm: FormListSchoolForm | null;
  // The following are only included when additional data is fetched
  guardian: Guardian | null;
  attendingSchool: AttendingSchool | null;
  answers: FormAnswers | null;
};

export type FormList = {
  totalCount: number;
  rows: FormListRow[];
};

export type FormAnswers = {
  [key: string]: FormAnswer | null;
};

export type SchoolRanking = {
  schoolId: string;
  rank?: number;
};

export type SchoolRankingAnswer = Array<SchoolRanking>;

export type FormAnswer =
  | string
  | Array<string>
  | Address
  | number
  | Date
  | FormCustomQuestionAnswer
  | DisclaimerSignature
  | SchoolRankingAnswer;

export type Address = {
  street_address: string;
  street_address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
};

export type FormCustomQuestionAnswer = {
  answersByQuestionId: {
    [key: string]: FormBasicAnswer | null;
  };
  answerBankId?: string;
  referenceId?: string | null;
  lastUsedAt?: string;
};

export type FormBasicAnswer = string | Array<string> | number | Date;

export type DisclaimerSignature = {
  signature: string;
  signedAt: string;
};
